import React, { useState } from "react";
import {
	Button,
	Dialog,
	DialogContent,
	Stepper,
	Step,
	StepLabel,
	Typography,
	Box,
	CircularProgress,
	Alert,
	IconButton,
	Paper,
	DialogTitle,
	DialogActions,
	TextField,
} from "@mui/material";
import { Smartphone, ContentCopy, CheckCircle as CheckCircleIcon, Warning as WarningIcon } from "@mui/icons-material";
import rootStyles from "../styles.module.scss";
import { NotistackServices, UserServices } from "shared/services";
import { OTPInput } from "components";
import { useAccount } from "shared/hooks";

interface RenderEnableContentProps {
	setupData: { secret: string; qrCodeUrl: string; backupCodes: string[] } | null;
	onClose?: () => void;
}

const RenderEnableContent: React.FC<RenderEnableContentProps> = ({ setupData, onClose }) => {
	const [loading, setLoading] = useState(false);
	const [step, setStep] = useState(0);
	const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
	const [error, setError] = useState<string | null>(null);

	const handleClose = () => {
		if (loading) return;
		onClose?.();
	};

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
		NotistackServices.showSuccessMessage("Copiado para a área de transferência!");
	};

	const handleVerifyEnable = async () => {
		setLoading(true);
		setError(null);
		try {
			const code = otp.join("");
			if (code.length !== 6) {
				throw new Error("Digite o código de 6 dígitos.");
			}
			const response = await UserServices.verify2FA(code);
			if (response.verified) {
				setStep(2); // Vai para Backup Codes
			} else {
				setError(response.error || "Código incorreto.");
			}
		} catch (err: any) {
			setError(err.message || "Erro na verificação.");
		} finally {
			setLoading(false);
		}
	};

	const steps = ["Gerar QRCode", "Confirmação", "Backup", "Concluído"];

	return (
		<>
			<DialogTitle>
				<Stepper
					activeStep={step}
					alternativeLabel
					sx={{
						"@media (max-width: 700px)": {
							".MuiStepLabel-labelContainer": { display: "none" },
						},
					}}
				>
					{steps.map((label) => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
			</DialogTitle>

			<DialogContent
				dividers
				sx={{ paddingY: 3 }}
			>
				{step === 0 && setupData && (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
						<Typography
							variant="body1"
							textAlign="center"
						>
							Escaneie o QR Code abaixo com seu aplicativo autenticador (Google Authenticator, Microsoft Authenticator, etc).
						</Typography>
						<Paper
							sx={{ p: 0.5, bgcolor: "white", borderRadius: 2, border: "1px solid #eee" }}
							elevation={8}
						>
							<img
								src={setupData.qrCodeUrl}
								alt="QR Code"
								style={{ maxWidth: 300 }}
							/>
						</Paper>
						<Typography
							variant="body2"
							color="textSecondary"
						>
							Ou digite o código manualmente:
						</Typography>

						<TextField
							defaultValue={setupData.secret}
							variant="outlined"
							size="small"
							fullWidth
							InputProps={{
								readOnly: true,
								endAdornment: (
									<IconButton
										size="small"
										onClick={() => copyToClipboard(setupData.secret)}
									>
										<ContentCopy fontSize="small" />
									</IconButton>
								),
							}}
						/>
					</Box>
				)}

				{step === 1 && (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
						<Typography
							variant="body1"
							textAlign="center"
						>
							Digite o código de 6 dígitos gerado pelo seu aplicativo para confirmar a ativação.
						</Typography>
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
							<OTPInput
								value={otp}
								onChange={setOtp}
								separator
							/>
						</Box>
						{error && <Alert severity="error">{error}</Alert>}
					</Box>
				)}

				{step === 2 && setupData && (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
						<Typography
							variant="h6"
							color="warning.main"
							sx={{ display: "flex", alignItems: "center", gap: 1 }}
						>
							<WarningIcon /> Guarde seus códigos de backup
						</Typography>
						<Typography
							variant="body2"
							textAlign="center"
						>
							Se você perder acesso ao seu dispositivo, poderá usar estes códigos para entrar na sua conta. Salve-os em um local seguro.
						</Typography>
						<Paper
							elevation={4}
							sx={{
								p: 2,
								display: "grid",
								gridTemplateColumns: "1fr 1fr",
								gap: 1,
								width: "100%",
								maxWidth: 400,
							}}
						>
							{setupData.backupCodes.map((code, index) => (
								<Typography
									key={index}
									variant="body2"
									sx={{ fontFamily: "monospace", textAlign: "center" }}
								>
									{code}
								</Typography>
							))}
						</Paper>
						<Button
							startIcon={<ContentCopy />}
							onClick={() => copyToClipboard(setupData.backupCodes.join("\n"))}
						>
							Copiar Códigos
						</Button>
					</Box>
				)}

				{step === 3 && (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
						<CheckCircleIcon
							color="success"
							sx={{ fontSize: 64 }}
						/>
						<Typography variant="h5">Ativação Bem Sucedida!</Typography>
						<Typography
							variant="body1"
							textAlign="center"
						>
							Sua conta agora está protegida com autenticação em duas etapas.
						</Typography>
					</Box>
				)}
			</DialogContent>

			<DialogActions>
				{step === 0 && (
					<>
						<Button
							color="inherit"
							onClick={handleClose}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							onClick={() => setStep(1)}
						>
							Próximo
						</Button>
					</>
				)}

				{step === 1 && (
					<>
						<Button
							color="inherit"
							onClick={() => setStep(0)}
						>
							Voltar
						</Button>
						<Button
							variant="contained"
							onClick={handleVerifyEnable}
							disabled={otp.join("").length !== 6 || loading}
							loading={loading}
						>
							Verificar e Ativar
						</Button>
					</>
				)}

				{step === 2 && (
					<>
						<Button
							variant="contained"
							onClick={() => setStep(3)}
						>
							Estou Ciente e Guardei os Códigos
						</Button>
					</>
				)}

				{step === 3 && (
					<>
						<Button
							variant="outlined"
							onClick={handleClose}
						>
							Fechar
						</Button>
					</>
				)}
			</DialogActions>
		</>
	);
};

interface RenderDisableContentProps {
	onClose?: () => void;
}

const RenderDisableContent: React.FC<RenderDisableContentProps> = ({ onClose }) => {
	const [loading, setLoading] = useState(false);
	const [step, setStep] = useState(0);
	const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
	const [error, setError] = useState<string | null>(null);

	const handleClose = () => {
		if (loading) return;
		onClose?.();
	};

	const handleVerifyDisable = async () => {
		setLoading(true);
		setError(null);
		try {
			const code = otp.join("");
			if (code.length !== 6) {
				throw new Error("Digite o código de 6 dígitos.");
			}
			await UserServices.disable2FA(code);
			setStep(1); // Sucesso disable
		} catch (err: any) {
			setError(err.message || "Código incorreto ou erro ao desativar.");
		}
		setLoading(false);
	};

	return (
		<>
			<DialogContent
				dividers
				sx={{ paddingY: 3 }}
			>
				{step === 0 && (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
						<Alert
							severity="warning"
							sx={{ width: "100%" }}
						>
							Atenção: Desativar a autenticação em duas etapas tornará sua conta menos segura.
						</Alert>
						<Typography
							variant="body1"
							textAlign="center"
						>
							Para confirmar, digite o código de 6 dígitos do seu autenticador.
						</Typography>
						<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
							<OTPInput
								value={otp}
								onChange={setOtp}
								separator
							/>
						</Box>
						{error && <Alert severity="error">{error}</Alert>}
					</Box>
				)}

				{step === 1 && (
					<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
						<CheckCircleIcon
							color="success"
							sx={{ fontSize: 64 }}
						/>
						<Typography variant="h5">Desativação Bem Sucedida</Typography>
						<Typography variant="body1">O 2FA foi removido da sua conta.</Typography>
					</Box>
				)}
			</DialogContent>
			<DialogActions>
				{step === 0 && (
					<>
						<Button
							onClick={handleClose}
							disabled={loading}
						>
							Cancelar
						</Button>
						<Button
							variant="contained"
							color="error"
							onClick={handleVerifyDisable}
							disabled={otp.join("").length !== 6 || loading}
							loading={loading}
						>
							Desativar 2FA
						</Button>
					</>
				)}
				{step === 1 && (
					<>
						<Button
							variant="outlined"
							onClick={handleClose}
						>
							Fechar
						</Button>
					</>
				)}
			</DialogActions>
		</>
	);
};

export const TwoFactorProcess = () => {
	const user = useAccount();
	const [open, setOpen] = useState(false);
	const [loading, setLoading] = useState(false);
	const [mode, setMode] = useState<"enable" | "disable">("enable");
	const [setupData, setSetupData] = useState<{ secret: string; qrCodeUrl: string; backupCodes: string[] } | null>(null);

	const handleOpenEnable = async () => {
		setLoading(true);
		try {
			const data = await UserServices.enable2FA();
			setSetupData(data);
			setMode("enable");
			setOpen(true);
		} catch (err: any) {
			NotistackServices.showErrorMessage(err.message || "Erro ao iniciar ativação do 2FA");
		} finally {
			setLoading(false);
		}
	};

	const handleOpenDisable = () => {
		setMode("disable");
		setOpen(true);
	};

	const handleClose = () => {
		if (loading) return;
		setOpen(false);
	};

	return (
		<>
			<div className={rootStyles.card}>
				<div className={rootStyles.btmRow}>
					<div className={rootStyles.info}>
						<div className={rootStyles.rowIcon}>
							<Smartphone />
						</div>
						<div className={rootStyles.text}>
							<h5>Autenticação em Duas Etapas (2FA)</h5>
							<p>Adicione uma camada extra de segurança à sua conta.</p>
						</div>
					</div>
					<Button
						variant="contained"
						color={user?.twoFactorEnabled ? "error" : "primary"}
						onClick={user?.twoFactorEnabled ? handleOpenDisable : handleOpenEnable}
						disabled={loading}
					>
						{loading ? (
							<CircularProgress
								size={24}
								color="inherit"
							/>
						) : user?.twoFactorEnabled ? (
							"Desativar"
						) : (
							"Ativar 2FA"
						)}
					</Button>
				</div>
			</div>
			<Dialog
				open={open}
				onClose={(event, reason) => {
					if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
						handleClose();
					}
				}}
				maxWidth="sm"
				fullWidth
				scroll="paper"
			>
				{mode === "enable" ? (
					<RenderEnableContent
						setupData={setupData}
						onClose={handleClose}
					/>
				) : (
					<RenderDisableContent onClose={handleClose} />
				)}
			</Dialog>
		</>
	);
};
